import React, {forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState} from "react";
import {TextField} from "@mui/material";
import {DateTimePicker} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {setSnackbar} from "../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../util/state/globalVariables";
import {useTranslation} from "react-i18next";

const KEY_BACKSPACE = "Backspace";
const KEY_DELETE = "Delete";
const KEY_ENTER = "Enter";
const KEY_TAB = "Tab";

const DateCustomEditorComponent = forwardRef((props: any, ref: any) => {

    // Use Context
    const {dispatchGlobalData} = useContext(GlobalContext);

    // Use Translation
    const {t} = useTranslation();

    const createInitialState = () => {
        let startValue;
        let highlightAllOnFocus = true;

        if (props.eventKey === KEY_BACKSPACE || props.eventKey === KEY_DELETE) {
            // if backspace or delete pressed, we clear the cell
            startValue = "";
        } else if (props.charPress) {
            // if a letter was pressed, we start with the letter
            startValue = props.charPress;
            highlightAllOnFocus = false;
        } else {
            // otherwise start with the current value
            startValue = props.value;
        }

        return {
            value: startValue,
            highlightAllOnFocus
        };
    };

    const initialState = createInitialState();

    const refDatePicker = useRef<HTMLInputElement>(null);
    const [highlightAllOnFocus, setHighlightAllOnFocus] = useState(initialState.highlightAllOnFocus);
    const [validDate, setValidDate] = useState(props.value);

    // focus on the input
    useEffect(() => {
        // get ref from React component
        const eInput = refDatePicker.current!;
        eInput.focus();
        if (highlightAllOnFocus) {

            setHighlightAllOnFocus(false);
        } else {
            // when we started editing, we want the caret at the end, not the start.
            //   a) when user hits F2
            //   b) when user hits a printable character
            const length = eInput.value ? eInput.value.length : 0;
            if (length > 0) {
                eInput.setSelectionRange(length, length);
            }
        }
        // eslint-disable-next-line
    }, []);

    /* Utility Methods */
    const cancelBeforeStart = props.charPress && "1234567890/APM".indexOf(props.charPress) < 0;

    useImperativeHandle(ref, () => {
        return {
            getValue() {
                return validDate;
            },

            // Gets called once before editing starts, to give editor a chance to
            // cancel the editing before it even starts.
            isCancelBeforeStart() {
                return cancelBeforeStart;
            }
            //
            // // Gets called once when editing is finished (eg if Enter is pressed).
            // // If you return true, then the result of the edit will be ignored.
            // isCancelAfterEnd() {
            //     // will reject the date if it's not a proper date.
            //     return dayjs(date).isValid();
            // }
        };
    });

    const finishedEditingPressed = (event: any) => {
        const key = event.key;
        return key === KEY_ENTER || key === KEY_TAB;
    };

    const isCharNumeric = (charStr: string) => {
        return /\d/.test(charStr);
    };

    const isKeyPressedNumeric = (event: any) => {
        const charStr = event.key;
        return isCharNumeric(charStr);
    };


    const deleteOrBackspace = (event: any) => {
        return [KEY_DELETE, KEY_BACKSPACE].indexOf(event.key) > -1;
    };

    const onKeyDown = (event: any) => {
        if (deleteOrBackspace(event)) {
            event.stopPropagation();
            return;
        }
        if (!finishedEditingPressed(event) && !isKeyPressedNumeric(event)) {
            if (event.preventDefault) event.preventDefault();
        }

        if (finishedEditingPressed(event)) {
            props.stopEditing();
        }
    };

    const onChange = (selectedDate: any) => {
        const selectedDateIsValid = dayjs(selectedDate).isValid();
        // modifica data di "to"
        if (props.colDef.field === "to") {
            const selectedDateDiffFrom = props.colDef.field === "to" ? dayjs(selectedDate).diff(dayjs(props.data.from), "minutes") : 0;
            // const selectedDateDiffNow = props.colDef.field === "to" ? dayjs(selectedDate).diff(dayjs()) : 0;

            // data di to inserita valida
            if (selectedDateIsValid) {
                let message = "";
                // data di to inserita valida ma minore della data di from
                if (selectedDateDiffFrom < 0) {
                    message = t("pricing:validation.greaterThan") + " " + dayjs(props.data.from).format("L LT");
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: message,
                                type: snackbarTypes.warning,
                                show: true,
                                autoClose: false
                            }
                        }
                    });
                }
                    // // data di to inserita valida maggiore della data di from ma nel passato
                    // else if (selectedDateDiffFrom > 0 && selectedDateDiffNow < 0) {
                    //     message = t("pricing:validation.greaterThanToday");
                    //     dispatchGlobalData({
                    //         type: setSnackbar,
                    //         payload: {
                    //             snackbar: {
                    //                 message: message,
                    //                 type: snackbarTypes.warning,
                    //                 show: true,
                    //                 autoClose: false
                    //             }
                    //         }
                    //     });
                    // }
                // else if (selectedDateDiffFrom > 0 && selectedDateDiffNow > 0) {
                else {
                    setValidDate(dayjs(selectedDate).toISOString());
                }
            }
        }
        // modifica data di "from"
        else {
            const toDiffSelectedDate = dayjs(props.data.to).diff(dayjs(selectedDate), "minutes");
            // const selectedDateDiffNow = dayjs(selectedDate).diff(dayjs());

            // data inserita valida
            if (selectedDateIsValid) {
                let message = "";
                // data di from inserita valida ma maggiore di to
                if (toDiffSelectedDate < 0) {
                    message = t("pricing:validation.lowerThan") + " " + dayjs(props.data.to).format("L LT");
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: message,
                                type: snackbarTypes.warning,
                                show: true,
                                autoClose: false
                            }
                        }
                    });
                }
                    // // data di from inserita valida minore della data di to ma nel passato
                    // else if (toDiffSelectedDate > 0 && selectedDateDiffNow < 0) {
                    //     message = t("pricing:validation.greaterThanToday");
                    //     dispatchGlobalData({
                    //         type: setSnackbar,
                    //         payload: {
                    //             snackbar: {
                    //                 message: message,
                    //                 type: snackbarTypes.warning,
                    //                 show: true,
                    //                 autoClose: false
                    //             }
                    //         }
                    //     });
                // }
                else {
                    setValidDate(dayjs(selectedDate).toISOString());
                }
            }
        }
    };

    return (
        <div>
            <DateTimePicker
                ref={refDatePicker}
                value={validDate}
                open={true}
                onChange={onChange}
                renderInput={(params) =>
                    <TextField
                        {...params}
                        fullWidth
                        id={"creationDateFrom"}
                        error={false}
                        onKeyDown={(event: any) => onKeyDown(event)}
                        helperText={""}
                    />
                }
                inputFormat={"L LT"}
            />
        </div>
    );
});

export default DateCustomEditorComponent;
